import { useEffect, useMemo, useState } from 'react';
import type { GenerationSlug, HealthResponse, Member, SortMode, Video, VideoKind } from './types.ts';
import { fetchHealth, fetchMembers, fetchVideos, youtubeWatchUrl } from './lib/api.ts';
import { useDebounce } from './lib/useDebounce.ts';
import { fmtIndexed } from './lib/format.ts';
import { track } from './lib/track.ts';
import { FALLBACK_STYLE, MEMBER_STYLES } from './data/styles.ts';
import { Header } from './components/Header.tsx';
import { FilterBar } from './components/FilterBar.tsx';
import { VideoCard } from './components/VideoCard.tsx';
import { EmptyState } from './components/EmptyState.tsx';

const RECENT_KEY = 'stelsearch.recent';
const RECENT_MAX = 6;
const PAGE_SIZE = 30;

export function App() {
  // ---- state ------------------------------------------------------------
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]'); }
    catch { return []; }
  });

  const [groupFilter, setGroupFilter] = useState<GenerationSlug | 'all'>('all');
  const [memberFilter, setMemberFilter] = useState<Set<string>>(new Set());
  const [kindFilter, setKindFilter] = useState<VideoKind | 'all'>('all');
  const [sort, setSort] = useState<SortMode>('newest');

  const debouncedQuery = useDebounce(query, 250);

  // ---- members + health (one-shot) --------------------------------------
  const [members, setMembers] = useState<Member[]>([]);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [membersLoaded, setMembersLoaded] = useState(false);

  useEffect(() => {
    fetchMembers().then((res) => {
      const flat: Member[] = [];
      for (const g of res.generations) {
        for (const m of g.members) {
          const style = MEMBER_STYLES[m.id] ?? FALLBACK_STYLE;
          flat.push({
            ...m,
            ...style,
            group: g.id,
            groupKr: g.nameKr,
          });
        }
      }
      setMembers(flat);
      setMembersLoaded(true);
    }).catch((e) => console.error('members load failed', e));

    fetchHealth().then(setHealth).catch((e) => console.error('health load failed', e));
  }, []);

  // ---- videos (refetch on filters / debounced query) --------------------
  const [videos, setVideos] = useState<Video[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-prune memberFilter when group narrows.
  useEffect(() => {
    if (groupFilter === 'all') return;
    setMemberFilter((prev) => {
      const allowed = new Set(members.filter((m) => m.group === groupFilter).map((m) => m.id));
      const next = new Set([...prev].filter((id) => allowed.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [groupFilter, members]);

  // If the user lands on 'relevance' and clears the query, fall back to newest.
  useEffect(() => {
    if (!debouncedQuery && sort === 'relevance') setSort('newest');
  }, [debouncedQuery, sort]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    fetchVideos(
      {
        q: debouncedQuery || undefined,
        group: groupFilter === 'all' ? undefined : groupFilter,
        members: memberFilter.size > 0 ? [...memberFilter] : undefined,
        kind: kindFilter === 'all' ? undefined : kindFilter,
        sort,
        limit: PAGE_SIZE,
      },
      ctrl.signal,
    )
      .then((res) => {
        setVideos(res.videos);
        setTotal(res.total);
        setNextCursor(res.nextCursor);
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setError(String(e));
      })
      .finally(() => setLoading(false));

    return () => ctrl.abort();
  }, [debouncedQuery, groupFilter, memberFilter, kindFilter, sort]);

  function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    track('load-more', {
      hasQuery: !!debouncedQuery,
      kind: kindFilter,
      group: groupFilter,
    });
    fetchVideos({
      q: debouncedQuery || undefined,
      group: groupFilter === 'all' ? undefined : groupFilter,
      members: memberFilter.size > 0 ? [...memberFilter] : undefined,
      kind: kindFilter === 'all' ? undefined : kindFilter,
      sort,
      cursor: nextCursor,
      limit: PAGE_SIZE,
    })
      .then((res) => {
        setVideos((prev) => [...prev, ...res.videos]);
        setNextCursor(res.nextCursor);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoadingMore(false));
  }

  // ---- recent searches --------------------------------------------------
  function commitRecent(q: string) {
    if (!q.trim()) return;
    track('search-submitted', {
      q: q.slice(0, 100),
      qLen: q.length,
      kind: kindFilter,
      group: groupFilter,
      memberCount: memberFilter.size,
    });
    setRecent((prev) => {
      const next = [q, ...prev.filter((x) => x !== q)].slice(0, RECENT_MAX);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  }
  function clearRecent() {
    setRecent([]);
    localStorage.removeItem(RECENT_KEY);
  }

  // ---- random pick ------------------------------------------------------
  function pickRandom() {
    if (videos.length === 0) return;
    const v = videos[Math.floor(Math.random() * videos.length)]!;
    track('random-pick', {
      videoId: v.id,
      memberId: v.memberId,
      kind: v.kind,
      poolSize: videos.length,
    });
    window.open(youtubeWatchUrl(v.id), '_blank', 'noopener');
  }

  // ---- reset ------------------------------------------------------------
  function resetFilters() {
    setQuery('');
    setGroupFilter('all');
    setMemberFilter(new Set());
    setKindFilter('all');
    setSort('newest');
  }

  // ---- member lookup ----------------------------------------------------
  const memberById = useMemo(() => {
    const m = new Map<string, Member>();
    for (const x of members) m.set(x.id, x);
    return m;
  }, [members]);

  const hasFilters =
    !!debouncedQuery || groupFilter !== 'all' || memberFilter.size > 0 || kindFilter !== 'all';

  const lastIndexed = health?.lastSyncedAt
    ? fmtIndexed(new Date(health.lastSyncedAt))
    : '—';

  return (
    <div className="ss-app">
      <Header
        query={query}
        onQueryChange={setQuery}
        onCommitRecent={commitRecent}
        recent={recent}
        onClearRecent={clearRecent}
        onPickRecent={(r) => { setQuery(r); commitRecent(r); }}
        onLogoClick={resetFilters}
        onRandom={pickRandom}
      />

      <FilterBar
        members={members}
        groupFilter={groupFilter}
        onGroupChange={(g) => {
          track('filter-changed', { type: 'group', value: g });
          setGroupFilter(g);
        }}
        memberFilter={memberFilter}
        onMemberToggle={(slug) => {
          setMemberFilter((prev) => {
            const next = new Set(prev);
            const adding = !next.has(slug);
            adding ? next.add(slug) : next.delete(slug);
            track('filter-changed', { type: 'member', value: slug, action: adding ? 'add' : 'remove' });
            return next;
          });
        }}
        onMemberClear={() => {
          track('filter-changed', { type: 'member', value: 'clear' });
          setMemberFilter(new Set());
        }}
        kindFilter={kindFilter}
        onKindChange={(k) => {
          track('filter-changed', { type: 'kind', value: k });
          setKindFilter(k);
        }}
        sort={sort}
        onSortToggle={() => {
          setSort((s) => {
            const next = s === 'newest' ? 'oldest' : 'newest';
            track('filter-changed', { type: 'sort', value: next });
            return next;
          });
        }}
      />

      <main className="ss-main">
        <div className="ss-results-head">
          <div className="ss-results-stat">
            <span className="ss-results-num">
              {total.toLocaleString('ko-KR')}
            </span>
            <span className="ss-results-of">
              {' '}/ {(health?.videoCount ?? total).toLocaleString('ko-KR')} 개의 영상
            </span>
            {hasFilters && (
              <button className="ss-results-reset" onClick={resetFilters}>
                필터 초기화
              </button>
            )}
          </div>
          <div className="ss-results-indexed">
            <span className="ss-results-indexed__dot" />
            마지막 갱신 {lastIndexed}
          </div>
        </div>

        {error && (
          <div className="ss-error">로딩 실패: {error}</div>
        )}

        {!loading && videos.length === 0 && membersLoaded ? (
          <EmptyState query={debouncedQuery} />
        ) : (
          <>
            <div className="ss-grid">
              {videos.map((v) => {
                const m = memberById.get(v.memberId);
                if (!m) return null;
                return (
                  <VideoCard
                    key={v.id}
                    video={v}
                    member={m}
                    query={debouncedQuery}
                  />
                );
              })}
            </div>

            {nextCursor && (
              <div className="ss-loadmore">
                <button onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? '불러오는 중…' : '더 보기'}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <footer className="ss-footer">
        <div className="ss-footer__inner">
          <span>stelsearch · 비공식 팬 메이드 검색기. 모든 영상 권리는 각 채널에 있습니다.</span>
        </div>
      </footer>
    </div>
  );
}
